import React, { useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { observer } from 'mobx-react';
import { withErrorBoundary } from 'react-error-boundary';
import { Link } from 'react-mobx-router5';
import {
  Breadcrumb,
  BreadcrumbItem,
  Modal,
  ModalVariant,
  Title,
} from '@patternfly/react-core';
import { useTranslation } from 'react-i18next';
import OrganizationList from 'components/ManagementReporting/OrganizationList';
import OrganizationForm from 'components/ManagementReporting/OrganizationForm';
import GenericViewError from 'components/Error/GenericViewError';
import useMobxStores from 'hooks/useMobxStores';
import { handleError } from 'services/ErrorService';
import './styles.scss';

const OrganizationAdmin = () => {
  const { routerStore, organizationStore } = useMobxStores();
  const { t } = useTranslation();
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const formCloseRequestRef = useRef<(() => void) | null>(null);

  const title = (
    (routerStore?.route as { params?: { title?: string } } | undefined)
      ?.params as { title?: string } | undefined
  )?.title;

  const isEditing = Boolean(organizationStore.editingOrganization.id);

  const openCreateModal = () => {
    organizationStore.resetEditOrganization();
    setIsFormModalOpen(true);
  };

  const openEditModal = (docId: string) => {
    organizationStore.editOrganization(docId);
    setIsFormModalOpen(true);
  };

  const closeFormModal = () => {
    organizationStore.resetEditOrganization();
    setIsFormModalOpen(false);
    setIsFormSubmitting(false);
  };

  const handleModalClose = () => {
    if (isFormSubmitting) {
      return;
    }

    if (formCloseRequestRef.current) {
      formCloseRequestRef.current();
      return;
    }

    closeFormModal();
  };

  return (
    <>
      <Helmet title={`${t(title ?? '')} - Red Hat Learning`} />
      <div className="container-grid-responsive management-reporting">
        <Breadcrumb className="management-reporting__breadcrumb">
          <BreadcrumbItem
            component={Link}
            to="home"
            onClick={() => routerStore.navigate('home')}
          >
            {t('Home')}
          </BreadcrumbItem>
          <BreadcrumbItem
            component={Link}
            to="admin"
            onClick={() => routerStore.navigate('admin')}
          >
            {t('Admin dashboard')}
          </BreadcrumbItem>
          <BreadcrumbItem isActive>{t('Management reporting')}</BreadcrumbItem>
        </Breadcrumb>
        <div className="management-reporting__header">
          <Title headingLevel="h1">{t(title ?? '')}</Title>
        </div>
        <OrganizationList
          onEdit={openEditModal}
          onCreate={openCreateModal}
          isCreateDisabled={isFormSubmitting}
          isFormModalOpen={isFormModalOpen}
        />
        <Modal
          variant={ModalVariant.large}
          title={isEditing ? t('Edit company') : t('Create company')}
          isOpen={isFormModalOpen}
          onClose={handleModalClose}
          showClose={!isFormSubmitting}
        >
          <OrganizationForm
            onClose={closeFormModal}
            onSubmittingChange={setIsFormSubmitting}
            onRegisterCloseRequest={(handler) => {
              formCloseRequestRef.current = handler;
            }}
          />
        </Modal>
      </div>
    </>
  );
};

export default withErrorBoundary(
  observer(OrganizationAdmin),
  GenericViewError,
  handleError,
);
